import {
  GRID_SIZE,
  MM_PER_METER,
  UNIT_TO_MM,
  DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN,
} from '../constants';
import type { MeasurementUnit } from '../types';

const METERS_PER_MEASUREMENT_UNIT: Record<MeasurementUnit, number> = {
  m: 1,
  mm: 0.001,
  in: 0.0254,
  ft: 0.3048,
};

export const metersToMeasurementUnit = (meters: number, unit: MeasurementUnit): number =>
  meters / METERS_PER_MEASUREMENT_UNIT[unit];

export const measurementUnitToMeters = (value: number, unit: MeasurementUnit): number =>
  value * METERS_PER_MEASUREMENT_UNIT[unit];

/** Compact label for on-canvas track dimensions (e.g. "1937mm", "6.4ft"). */
export const formatMeasurementLabel = (meters: number, unit: MeasurementUnit): string => {
  if (!Number.isFinite(meters) || meters <= 0) return '';
  const v = metersToMeasurementUnit(meters, unit);
  switch (unit) {
    case 'mm':
      return `${Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10}mm`;
    case 'm':
      if (Math.abs(v) >= 10) return `${Math.round(v * 10) / 10}m`;
      return `${Math.round(v * 1000) / 1000}m`;
    case 'in':
    case 'ft':
      return `${Math.round(v * 100) / 100}${unit}`;
    default:
      return `${v}${unit}`;
  }
};

export const measurementUnitStep = (unit: MeasurementUnit): number => {
  switch (unit) {
    case 'mm':
      return 1;
    case 'm':
      return 0.01;
    case 'in':
    case 'ft':
      return 0.01;
    default:
      return 0.01;
  }
};

export type DxfUnit = 'mm' | 'cm' | 'm' | 'in' | 'ft';

export type ViewTransform = {
  stagePos: { x: number; y: number };
  stageScale: number;
};

export type EffectiveCalibration = {
  metersPerUnit: number;
  unitsPerMeter: number;
  millimetersPerUnit: number;
};

const DEFAULT_METERS_PER_WORLD_UNIT_WITHOUT_FLOORPLAN =
  1 / DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN;

const METERS_PER_DXF_UNIT: Record<DxfUnit, number> = {
  mm: 0.001,
  cm: 0.01,
  m: 1,
  in: 0.0254,
  ft: 0.3048,
};

// Common DXF INSUNITS values
export const insUnitsToDxfUnit = (code?: number): DxfUnit => {
  switch (code) {
    case 4:
      return 'mm';
    case 5:
      return 'cm';
    case 6:
      return 'm';
    case 1:
      return 'in';
    case 2:
      return 'ft';
    default:
      return 'mm';
  }
};

export const dxfUnitToDxfUnitsPerMeter = (unit: DxfUnit): number =>
  1 / METERS_PER_DXF_UNIT[unit];

export const dxfUnitsToMeters = (units: number, unit: DxfUnit): number =>
  units * METERS_PER_DXF_UNIT[unit];

export const metersToDxfUnits = (meters: number, unit: DxfUnit): number =>
  meters / METERS_PER_DXF_UNIT[unit];

export const metersToPixels = (meters: number, pixelsPerMeter: number): number =>
  meters * pixelsPerMeter;

export const pixelsToMeters = (pixels: number, pixelsPerMeter: number): number =>
  pixels / pixelsPerMeter;

// In this editor, world units are canvas units at stageScale=1.
export const screenToWorld = (
  point: { x: number; y: number },
  transform: ViewTransform
): { x: number; y: number } => ({
  x: (point.x - transform.stagePos.x) / transform.stageScale,
  y: (point.y - transform.stagePos.y) / transform.stageScale,
});

export const worldToScreen = (
  point: { x: number; y: number },
  transform: ViewTransform
): { x: number; y: number } => ({
  x: point.x * transform.stageScale + transform.stagePos.x,
  y: point.y * transform.stageScale + transform.stagePos.y,
});

// Converts normalized canvas/world units to meters using import calibration.
// scaleFactor = raw DXF units per 1 world unit.
export const worldUnitsToMeters = (
  worldUnits: number,
  scaleFactor: number,
  dxfUnitsPerMeter: number
): number => {
  if (scaleFactor <= 0 || dxfUnitsPerMeter <= 0) return worldUnits;
  return (worldUnits * scaleFactor) / dxfUnitsPerMeter;
};

export const metersToWorldUnits = (
  meters: number,
  scaleFactor: number,
  dxfUnitsPerMeter: number
): number => {
  if (scaleFactor <= 0 || dxfUnitsPerMeter <= 0) return meters;
  return (meters * dxfUnitsPerMeter) / scaleFactor;
};

// Authoritative editor scale: 1 world unit = 11 mm, 1 grid = 10 world units.
export const worldUnitsToAuthoritativeMm = (worldUnits: number): number =>
  worldUnits * UNIT_TO_MM;

export const worldUnitsToAuthoritativeMeters = (worldUnits: number): number =>
  worldUnitsToAuthoritativeMm(worldUnits) / MM_PER_METER;

export const metersToAuthoritativeWorldUnits = (meters: number): number =>
  (meters * MM_PER_METER) / UNIT_TO_MM;

export const worldUnitsToAuthoritativeGrids = (worldUnits: number): number =>
  worldUnits / GRID_SIZE;

/** True when imported DXF geometry is loaded and DXF scale inputs should apply. */
export const shouldUseDxfCalibration = (
  hasBounds: boolean,
  importedPolylineCount: number
): boolean => hasBounds && importedPolylineCount > 0;

// Uses imported DXF calibration when present; otherwise falls back to
// 1 meter = DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN world units (no floor-plan mode).
export const getEffectiveCalibration = (
  scaleFactor?: number,
  dxfUnitsPerMeter?: number
): EffectiveCalibration => {
  const hasDxfCalibration =
    Number.isFinite(scaleFactor) &&
    Number.isFinite(dxfUnitsPerMeter) &&
    (scaleFactor as number) > 0 &&
    (dxfUnitsPerMeter as number) > 0;

  if (hasDxfCalibration) {
    const metersPerUnit = (scaleFactor as number) / (dxfUnitsPerMeter as number);
    return {
      metersPerUnit,
      unitsPerMeter: 1 / metersPerUnit,
      millimetersPerUnit: metersPerUnit * MM_PER_METER,
    };
  }

  const metersPerUnit = DEFAULT_METERS_PER_WORLD_UNIT_WITHOUT_FLOORPLAN;
  return {
    metersPerUnit,
    unitsPerMeter: 1 / metersPerUnit,
    millimetersPerUnit: metersPerUnit * MM_PER_METER,
  };
};

export const worldUnitsToLayoutMeters = (worldUnits: number): number =>
  worldUnits * DEFAULT_METERS_PER_WORLD_UNIT_WITHOUT_FLOORPLAN;

export const layoutMetersToWorldUnits = (meters: number): number =>
  meters * DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN;

export const worldUnitsToLayoutMillimeters = (worldUnits: number): number =>
  worldUnitsToLayoutMeters(worldUnits) * MM_PER_METER;

export const layoutMillimetersToWorldUnits = (millimeters: number): number =>
  layoutMetersToWorldUnits(millimeters / MM_PER_METER);

export const worldUnitsToEffectiveMeters = (
  worldUnits: number,
  scaleFactor?: number,
  dxfUnitsPerMeter?: number
): number => {
  const calibration = getEffectiveCalibration(scaleFactor, dxfUnitsPerMeter);
  return worldUnits * calibration.metersPerUnit;
};

export const metersToEffectiveWorldUnits = (
  meters: number,
  scaleFactor?: number,
  dxfUnitsPerMeter?: number
): number => {
  const calibration = getEffectiveCalibration(scaleFactor, dxfUnitsPerMeter);
  return meters * calibration.unitsPerMeter;
};

export const worldUnitsToEffectiveGrids = (worldUnits: number): number =>
  worldUnits / GRID_SIZE;
